import type { StationTargetRelation } from "./stationStore"

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null

/**
 * Redis / SSE aggregate: one `target_id` with a `stations` list (per-target snapshot).
 * GET bootstrap may return one object, or an array of such objects.
 */
export const parseTargetStationRelationAggregatePayload = (
  data: unknown,
): {
  targetId: string
  active: boolean
  relations: Record<string, StationTargetRelation>
} | null => {
  if (!isRecord(data)) {
    return null
  }

  const targetId = data.target_id
  if (typeof targetId !== "string" || targetId.length === 0) {
    return null
  }

  if (!Array.isArray(data.stations)) {
    return null
  }

  const active = typeof data.active === "boolean" ? data.active : false

  const relations: Record<string, StationTargetRelation> = {}
  for (const row of data.stations) {
    if (!isRecord(row)) {
      return null
    }
    const stationId = row.station_id
    if (typeof stationId !== "string" || stationId.length === 0) {
      return null
    }
    relations[stationId] = {
      station_id: stationId,
      target: { id: targetId, active },
      manual_stop: row.manual_stop === true,
    }
  }

  return { targetId, active, relations }
}

function isAggregateShape(record: Record<string, unknown>): boolean {
  return (
    typeof record.target_id === "string" &&
    record.target_id.length > 0 &&
    Array.isArray(record.stations)
  )
}

const normalizeStationTargetRelation = (
  payload: unknown,
): StationTargetRelation | null => {
  if (!isRecord(payload)) {
    return null
  }

  const stationId = payload.station_id
  if (typeof stationId !== "string" || stationId.length === 0) {
    return null
  }

  const target = payload.target
  if (target === null) {
    return {
      station_id: stationId,
      target: null,
    }
  }

  if (!isRecord(target)) {
    return null
  }

  const targetId = target.id
  const active = target.active
  if (
    typeof targetId !== "string" ||
    targetId.length === 0 ||
    typeof active !== "boolean"
  ) {
    return null
  }

  return {
    station_id: stationId,
    target: {
      id: targetId,
      active,
    },
    ...(typeof payload.manual_stop === "boolean"
      ? { manual_stop: payload.manual_stop }
      : {}),
  }
}

export interface NormalizeStationTargetRelationsResult {
  relations: Record<string, StationTargetRelation>
  hasInvalidEntries: boolean
  recognizedShape: boolean
}

export const normalizeStationTargetRelationsPayload = (
  payload: unknown,
): NormalizeStationTargetRelationsResult => {
  if (Array.isArray(payload)) {
    if (payload.length === 0) {
      return {
        relations: {},
        hasInvalidEntries: false,
        recognizedShape: true,
      }
    }

    const first = payload[0]
    if (isRecord(first) && isAggregateShape(first)) {
      let hasInvalidEntries = false
      const relations: Record<string, StationTargetRelation> = {}

      for (const value of payload) {
        const parsed = parseTargetStationRelationAggregatePayload(value)
        if (!parsed) {
          hasInvalidEntries = true
          continue
        }
        Object.assign(relations, parsed.relations)
      }

      return {
        relations,
        hasInvalidEntries,
        recognizedShape: true,
      }
    }

    let hasInvalidEntries = false
    const relations = Object.fromEntries(
      payload.flatMap((value) => {
        const relation = normalizeStationTargetRelation(value)
        if (!relation) {
          hasInvalidEntries = true
          return []
        }

        return [[relation.station_id, relation] as const]
      }),
    )

    return {
      relations,
      hasInvalidEntries,
      recognizedShape: true,
    }
  }

  if (isRecord(payload) && isAggregateShape(payload)) {
    const parsed = parseTargetStationRelationAggregatePayload(payload)
    if (parsed) {
      return {
        relations: parsed.relations,
        hasInvalidEntries: false,
        recognizedShape: true,
      }
    }
  }

  const singleRelation = normalizeStationTargetRelation(payload)
  if (singleRelation) {
    return {
      relations: { [singleRelation.station_id]: singleRelation },
      hasInvalidEntries: false,
      recognizedShape: true,
    }
  }

  if (!isRecord(payload)) {
    return {
      relations: {},
      hasInvalidEntries: false,
      recognizedShape: false,
    }
  }

  const values = Object.values(payload)
  if (values.length === 0) {
    return {
      relations: {},
      hasInvalidEntries: false,
      recognizedShape: true,
    }
  }

  let hasInvalidEntries = false
  const relations: Record<string, StationTargetRelation> = {}

  for (const value of values) {
    const aggregate = parseTargetStationRelationAggregatePayload(value)
    if (aggregate) {
      Object.assign(relations, aggregate.relations)
      continue
    }

    const relation = normalizeStationTargetRelation(value)
    if (!relation) {
      hasInvalidEntries = true
      continue
    }
    relations[relation.station_id] = relation
  }

  return {
    relations,
    hasInvalidEntries,
    recognizedShape: Object.keys(relations).length > 0 || !hasInvalidEntries,
  }
}
